import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getListings } from '../firebase';
import SearchBar from '../components/SearchBar';
import FiltersModal from '../components/FiltersModal';
import './Browse.css';

function Browse() {
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ 
    location: '', 
    minPrice: '', 
    maxPrice: '', 
    beds: '',
    guests: '',
    checkIn: '',
    checkOut: ''
  });
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [currentSearch, setCurrentSearch] = useState(null);
  const navigate = useNavigate();

  const applyFilters = useCallback(() => {
    let filtered = listings;
    if (filters.location) filtered = filtered.filter((l) => l.location.toLowerCase().includes(filters.location.toLowerCase()));
    if (filters.minPrice) filtered = filtered.filter((l) => l.price >= parseInt(filters.minPrice));
    if (filters.maxPrice) filtered = filtered.filter((l) => l.price <= parseInt(filters.maxPrice));
    if (filters.beds) filtered = filtered.filter((l) => l.beds >= parseInt(filters.beds));
    if (filters.guests) filtered = filtered.filter((l) => (l.beds || 1) >= parseInt(filters.guests));
    setFilteredListings(filtered);
  }, [listings, filters]);

  useEffect(() => {
    fetchListings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const fetchListings = async () => {
    try {
      const allListings = await getListings();
      setListings(allListings);
      setFilteredListings(allListings);
    } catch (error) {
      alert('Failed to fetch listings');
    }
    setLoading(false);
  };

  const handleSearch = (searchData) => {
    setCurrentSearch(searchData);
    setFilters({
      location: searchData.location,
      minPrice: '',
      maxPrice: '',
      beds: '',
      guests: searchData.guests,
      checkIn: searchData.checkIn,
      checkOut: searchData.checkOut
    });
    setSearchPerformed(true);
  };

  const handleApplyFilters = (appliedFilters) => {
    setFilters({
      ...currentSearch,
      ...appliedFilters,
      minPrice: appliedFilters.minPrice || filters.minPrice,
      maxPrice: appliedFilters.maxPrice || filters.maxPrice,
      beds: appliedFilters.bedrooms || filters.beds
    });
  };

  if (loading) return <div className="loading">Loading listings...</div>;

  return (
    <div className="browse-page">
      <div className="browse-search-section">
        {searchPerformed ? (
          <SearchBar 
            isCompact={true} 
            onOpenFilters={() => setShowFiltersModal(true)}
          />
        ) : (
          <SearchBar onSearch={handleSearch} />
        )}
      </div>

      {searchPerformed ? (
        <div className="browse-container results-view">
          <div className="listings-section">
            <div className="results-header">
              <p className="results-count">✨ {filteredListings.length} stays available</p>
            </div>
            <div className="listings-grid">
              {filteredListings.length === 0 ? (
                <p className="no-listings">No listings match your criteria</p>
              ) : (
                filteredListings.map((listing) => (
                  <div key={listing.id} className="listing-card" onClick={() => navigate(`/listing/${listing.id}`)}>
                    <img src={listing.photos[0]} alt={listing.title} />
                    <div className="card-content">
                      <h3>{listing.title}</h3>
                      <p className="location">{listing.location}</p>
                      <div className="card-meta">
                        <span>🛏️ {listing.beds} beds</span>
                        <span>🚿 {listing.baths} baths</span>
                      </div>
                      <p className="price">${listing.price}/month</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="browse-container">
          <div className="browse-sidebar">
            <div className="filters-section">
              <h3>Filters</h3>
              <div className="filter-group">
                <label>Bedrooms</label>
                <input type="number" name="beds" placeholder="Min bedrooms" value={filters.beds} onChange={(e) => setFilters({...filters, beds: e.target.value})} min="1" />
              </div>

              <div className="filter-group">
                <label>Price Range</label>
                <div className="filter-row">
                  <input type="number" name="minPrice" placeholder="Min" value={filters.minPrice} onChange={(e) => setFilters({...filters, minPrice: e.target.value})} min="0" />
                  <input type="number" name="maxPrice" placeholder="Max" value={filters.maxPrice} onChange={(e) => setFilters({...filters, maxPrice: e.target.value})} min="0" />
                </div>
              </div>

              <p className="filter-results">✨ {filteredListings.length} stays available</p>
            </div>
          </div>

          <div className="listings-section">
            <div className="listings-grid">
              {filteredListings.length === 0 ? (
                <p className="no-listings">No listings match your criteria</p>
              ) : (
                filteredListings.map((listing) => (
                  <div key={listing.id} className="listing-card" onClick={() => navigate(`/listing/${listing.id}`)}>
                    <img src={listing.photos[0]} alt={listing.title} />
                    <div className="card-content">
                      <h3>{listing.title}</h3>
                      <p className="location">{listing.location}</p>
                      <div className="card-meta">
                        <span>🛏️ {listing.beds} beds</span>
                        <span>🚿 {listing.baths} baths</span>
                      </div>
                      <p className="price">${listing.price}/month</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <FiltersModal 
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        onApplyFilters={handleApplyFilters}
        currentFilters={filters}
      />
    </div>
  );
}

export default Browse;
